import { setCookie, getCookie } from './cookies';
import {
  COUNT_DAYS_SAVE_COOKIE,
  KEY_GOOGLE_CLICK_ID,
  KEY_REFERRAL_CODE,
  KEY_APPSFLYER_UID,
  KEY_APPSFLYER_SOURCE_PLATFORM,
} from '../constants';

export const addDaysDate = (countDay: number, date = new Date()) => new Date(date.setDate(date.getDate() + +countDay));

export const getLengthError = (error: string, min: number, max: number) => {
  return error.replace('{min}', min.toString()).replace('{max}', max.toString());
};

export const getFixedLengthError = (error: string, length: number) => {
  return error.replace('{length}', length.toString());
};

export const getPrincipalErrorValue = (error: string, replaceString: string, amount: string) => {
  return error.replace(replaceString, amount);
};

export const getCodeByName = (object: any, name: any, booleanValue = false) => {
  const codeValue = object?.find((item: any) => item.code === name)?.code;
  return booleanValue ? JSON.parse(codeValue) : codeValue;
};

export const getNameByCode = (object: any, name: any) => {
  const codeValue = object?.find((item: any) => item.code === name)?.name;
  return codeValue;
};

const BANK_CODE_LENGTH = 3;

export const getBankByAccountNumber = <TBank extends { code: string }>(
  banks: readonly TBank[],
  accountNumber?: string | null,
): TBank | undefined => {
  const prefix = accountNumber?.replace(/\D/g, '').substring(0, BANK_CODE_LENGTH);
  if (prefix?.length !== BANK_CODE_LENGTH) return undefined;

  return banks.find(bank => bank.code === prefix);
};

export const getBankCodeByAccountNumber = (
  banks: ReadonlyArray<{ code: string }>,
  accountNumber?: string | null,
): string | undefined => getBankByAccountNumber(banks, accountNumber)?.code;

export const redirectToBlob = (blob: any) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('target', '_blank');
  link.click();
  link.remove();
};

/** Extracts the file uuid from a BE file link, e.g. `/api/fo/v1/file/{uuid}`. */
export const getFileUuidFromUrl = (url: string): string => url.split('/').filter(Boolean).pop() ?? '';

export const downloadBlobFile = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 10000);
};

export const dataURItoBlob = (dataURI: any) => {
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) {
    byteString = atob(dataURI.split(',')[1]);
  } else {
    byteString = unescape(dataURI.split(',')[1]);
  }
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], { type: mimeString });
};

interface CameraResolutionProfile {
  width?: number;
  height?: number;
}

const cameraResolutionProfiles: CameraResolutionProfile[] = [
  { width: 1280, height: 960 },
  { width: 1280, height: 720 },
  { width: 960, height: 720 },
  { width: 960, height: 540 },
  { width: 640, height: 480 },
  {},
];

const getVideoConstraints = (mode: any, profile: CameraResolutionProfile): MediaTrackConstraints => {
  const facingMode = typeof mode === 'string' ? { ideal: mode } : mode;

  return {
    facingMode,
    ...(profile.width ? { width: { ideal: profile.width } } : {}),
    ...(profile.height ? { height: { ideal: profile.height } } : {}),
  };
};

export const getUserMedia = async (mode: any) => {
  let lastError: unknown = null;

  for (const profile of cameraResolutionProfiles) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: getVideoConstraints(mode, profile),
      });
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
};

export const getMediaTrackResolutionLabel = (track: MediaStreamTrack | undefined): string | null => {
  if (!track) {
    return null;
  }

  const settings = track.getSettings();
  const { width, height } = settings;

  if (!width || !height) {
    return null;
  }

  return `${width}x${height}`;
};

export const getMediaTrackFacingModeLabel = (track: MediaStreamTrack | undefined): string | null => {
  if (!track) {
    return null;
  }

  const settings = track.getSettings();
  const { facingMode } = settings;

  if (typeof facingMode !== 'string' || !facingMode) {
    return null;
  }

  return facingMode;
};

export const setReferralCode = () => {
  const url = new URL(window.location.href);
  // direct campaign
  const referralCodeDirect = url.searchParams.get(KEY_REFERRAL_CODE);
  if (referralCodeDirect) {
    setCookie(KEY_REFERRAL_CODE, referralCodeDirect);
  }
  // campaign through WP landing
  if (document.referrer.length) {
    const referralCodeRef = url.searchParams.get(KEY_REFERRAL_CODE);
    if (referralCodeRef) {
      setCookie(KEY_REFERRAL_CODE, referralCodeRef);
    }
  }
};

// NOTE: is it used?
export const setAppsflyerUid = () => {
  const url = new URL(window.location.href);
  // direct campaign
  const appsflyerUidDirect = url.searchParams.get(KEY_APPSFLYER_UID);
  if (appsflyerUidDirect) {
    const appsflyerData = appsflyerUidDirect.split('_');
    setCookie(KEY_APPSFLYER_UID, appsflyerData[0]);
    appsflyerData[1] && setCookie(KEY_APPSFLYER_SOURCE_PLATFORM, appsflyerData[1]);
  }
  // campaign through WP landing
  if (document.referrer.length) {
    const referrerUrlParams = new URLSearchParams(new URL(document.referrer).search);
    const appsflyerUidRef = referrerUrlParams.get(KEY_APPSFLYER_UID);
    if (appsflyerUidRef) {
      const appsflyerData = appsflyerUidRef.split('_');
      setCookie(KEY_APPSFLYER_UID, appsflyerData[0]);
      appsflyerData[1] && setCookie(KEY_APPSFLYER_SOURCE_PLATFORM, appsflyerData[1]);
    }
  }
};

export const setGoogleClickId = () => {
  const countDay = addDaysDate(COUNT_DAYS_SAVE_COOKIE);
  const url = new URL(window.location.href);
  // direct ad-campaign
  const googleClickIdDirect = url.searchParams.get('gclid');
  if (googleClickIdDirect) {
    setCookie(KEY_GOOGLE_CLICK_ID, googleClickIdDirect, countDay);
  }
  // ad-campaigt through WP landing
  if (document.referrer.length) {
    const googleClickIdRef = url.searchParams.get('gclid');
    if (googleClickIdRef) {
      setCookie(KEY_GOOGLE_CLICK_ID, googleClickIdRef, countDay);
    }
  }
};

const getGoogleSessionId = () => {
  const cookie = getCookie('_ga_56BM2TEXMQ');
  return cookie ? cookie.split('.').slice(2, -5).join('.') : null;
};

const getGoogleClientId = () => {
  const cookie = getCookie('_ga');
  return cookie ? cookie.split('.').slice(-2).join('.') : null;
};

export const getMarketingData = () => {
  const googleSessionId = getGoogleSessionId();
  const googleClientId = getGoogleClientId();
  const facebookSessionId = getCookie('_fbp') ?? null;
  const facebookClientId = getCookie('_fbc') ?? null;
  const googleClickId = getCookie('gclid') ?? null;
  return {
    google_client_id: googleClientId,
    google_session_id: googleSessionId,
    facebook_client_id: facebookClientId,
    facebook_session_id: facebookSessionId,
    google_click_id: googleClickId,
  };
};

export const startCredoLabTracking = () => {
  // const url: string = process.env.REACT_APP_CREDOLAB_URL ?? '';
  // const authKey: string = process.env.REACT_APP_CREDOLAB_AUTH_KEY ?? '';
  // const credoAppService = new CredoAppService(url, authKey);
  // credoAppService.startTracking();
};

export const stopAndUploadCredoLabTracking = async () => {
  // const url: string = process.env.REACT_APP_CREDOLAB_URL ?? '';
  // const authKey: string = process.env.REACT_APP_CREDOLAB_AUTH_KEY ?? '';
  // const credoAppService = new CredoAppService(url, authKey);
  // const referenceNumber = getStorageItem(KEY_USER_UUID) || null;
  // if (referenceNumber) {
  //   await credoAppService.stopTrackingAndCompleteAsync(referenceNumber);
  //   removeStorageItem(KEY_USER_UUID);
  // }
};

export const setFirebaseToken = () => {
  const url = new URL(window.location.href);
  const firebaseToken = url.searchParams.get('firebase_token');
  if (firebaseToken) {
    setCookie('firebase_token', firebaseToken);
  }
};

export const getUserVideoInput = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(device => device.kind === 'videoinput');
  } catch (error) {
    return false;
  }
};

export const isValuePresent = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

/** Trims promo code; empty / whitespace-only becomes undefined. */
export const normalizePromoCode = (promoCode?: string | null): string | undefined => {
  if (promoCode == null) {
    return undefined;
  }
  const trimmed = promoCode.trim();
  return trimmed || undefined;
};
